import { NavLink, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { Menu, X, ArrowUpRight, Mail, Phone } from "lucide-react";
import { IDS } from "@/lib/testIds";
import { useLang } from "@/i18n/LangContext";
import LangToggle from "@/components/layout/LangToggle";
import Logo from "@/components/brand/Logo";
import { ConsultPopover, DIRECT_EMAIL, DIRECT_PHONE, DIRECT_PHONE_TEL } from "@/components/layout/ConsultPopover";

export default function Navbar() {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  const LINKS = [
    { to: "/", label: t.nav.home, id: IDS.nav.home },
    { to: "/about", label: t.nav.about, id: IDS.nav.about },
    { to: "/practice-areas", label: t.nav.practice, id: IDS.nav.practice },
    { to: "/team", label: t.nav.team, id: IDS.nav.team },
    { to: "/matters", label: "Matters", id: "nav-matters" },
    { to: "/insights", label: t.nav.insights, id: IDS.nav.insights },
    { to: "/contact", label: t.nav.contact, id: IDS.nav.contact },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  return (
    <>
      <motion.nav
        data-testid={IDS.nav.root}
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, delay: 0.2, ease: [0.76, 0, 0.24, 1] }}
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 backdrop-blur-xl ${scrolled ? "bg-[#050505]/85 border-b border-[#222]" : "bg-[#050505]/55 border-b border-[#141414]/70"}`}
      >
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
          <Link to="/" data-testid={IDS.nav.logo} className="group flex items-center gap-4">
            <Logo height={36} />
            <div className="hidden md:block leading-none pl-4 border-l border-[#333]">
              <div className="eyebrow mt-0.5">UAE · India · USA</div>
            </div>
          </Link>

          <div className="hidden lg:flex items-center gap-8">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.id}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `link-hover font-mono text-[11px] tracking-[0.22em] uppercase transition-colors ${isActive ? "text-[#F4F1EB]" : "text-[#A19D94] hover:text-[#F4F1EB]"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <LangToggle />
            <div className="hidden md:block">
              <ConsultPopover triggerTestId={IDS.nav.cta} buttonLabel={t.nav.consult} />
            </div>
            <button
              onClick={() => setOpen((o) => !o)}
              data-testid={IDS.nav.menuToggle}
              className="lg:hidden w-10 h-10 border border-[#222] flex items-center justify-center"
              aria-label="Menu"
            >
              {open ? <X size={16} /> : <Menu size={16} />}
            </button>
          </div>
        </div>
      </motion.nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-40 bg-[#050505] pt-24 px-6 lg:hidden"
          >
            <div className="flex flex-col divide-y divide-[#141414]">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 + i * 0.05, ease: [0.76, 0, 0.24, 1] }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`${l.id}-mobile`}
                    className="flex items-center justify-between py-6 font-serif text-3xl"
                  >
                    <span>{l.label}</span>
                    <span className="chapter-num">0{i + 1}</span>
                  </NavLink>
                </motion.div>
              ))}
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5, ease: [0.76, 0, 0.24, 1] }}
              className="mt-10 border border-[#222] p-6"
            >
              <div className="chapter-num text-[#C84C35] mb-4">Direct</div>
              <a href={`mailto:${DIRECT_EMAIL}`} className="flex items-start gap-3 py-3 border-b border-[#141414]" data-testid="mobile-direct-email">
                <Mail size={14} strokeWidth={1.25} className="text-[#C84C35] mt-1 shrink-0" />
                <div className="min-w-0">
                  <div className="chapter-num text-[#66635C]">Email</div>
                  <div className="text-[#F4F1EB] mt-1 break-all">{DIRECT_EMAIL}</div>
                </div>
              </a>
              <a href={`tel:${DIRECT_PHONE_TEL}`} className="flex items-start gap-3 py-3" data-testid="mobile-direct-phone">
                <Phone size={14} strokeWidth={1.25} className="text-[#C84C35] mt-1 shrink-0" />
                <div>
                  <div className="chapter-num text-[#66635C]">Phone</div>
                  <div className="text-[#F4F1EB] mt-1">{DIRECT_PHONE}</div>
                </div>
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
